//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

// TODO: PD-284133 - Delete this file once the MultiHandleHashtagTracking feature flag is released

import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import * as instagramHashtagsApi from '@apis/instagram-hashtag/instagramHashtagApi';
import {
  IAliasedDateRange,
  getAliasedDateRange,
} from '@components/date-range-picker/dateRangePickerModels';
import { IInstagramHashtagPageState } from '@instagram-account/redux/instagram-hashtag/hashtag-page/instagramHashtagPageModel';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import DateString from '@models/DateString';
import InstagramHashtag from '@models/InstagramHashtag';
import TimeZoneId from '@models/TimeZoneId';
import { navigateToState } from '@react-bridge/wrappers/navigation';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import { getUserTimeZone } from '@redux/selectors/getUserTimeZone';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';

import { crl8 } from 'app/curalate.globals';

const initialState: IInstagramHashtagPageState = {
  dateRange: getAliasedDateRange(
    'last7Days',
    crl8.session?.timeZone.id || TimeZoneId.Utc,
  ),
  errors: {
    fetchInstagramHashtagTrackedDateError: undefined,
    fetchInstagramHashtagTrackedHashtagsError: undefined,
    removeTrackedHashtagsError: undefined,
  },
  flags: {
    isFetchingInstagramHashtagTrackedDate: false,
    isFetchingInstagramHashtagTrackedHashtags: false,
    isRemovingTrackeHashtags: false,
  },
  hashtag: InstagramHashtag(''),
  trackedHashtags: [],
  trackedDate: DateString.getToday(crl8.session?.timeZone.id || TimeZoneId.Utc),
};

const { actions, reducer } = createSlice({
  name: SliceNames.InstagramHashtagPage,
  initialState,
  reducers: {
    setInstagramHashtagPageSource(
      state,
      action: PayloadAction<InstagramHashtag>,
    ) {
      state.hashtag = action.payload;
    },
    setInstagramHashtagDateRange(
      state,
      action: PayloadAction<IAliasedDateRange>,
    ) {
      state.dateRange = action.payload;
    },
    requestInstagramHashtagTrackedHashtags(state) {
      state.flags.isFetchingInstagramHashtagTrackedHashtags = true;
    },
    receiveInstagramHashtagTrackedHashtagsSucceed(
      state,
      action: PayloadAction<InstagramHashtag[]>,
    ) {
      state.flags.isFetchingInstagramHashtagTrackedHashtags = false;
      state.trackedHashtags = [...action.payload];
    },
    receiveInstagramHashtagTrackedHashtagsFailed(
      state,
      action: PayloadAction<string | undefined>,
    ) {
      state.flags.isFetchingInstagramHashtagTrackedHashtags = false;
      state.errors.fetchInstagramHashtagTrackedHashtagsError = action.payload;
    },
    requestInstagramHashtagTrackedDate(state) {
      state.flags.isFetchingInstagramHashtagTrackedDate = true;
    },
    receiveInstagramHashtagTrackedDateSucceed(
      state,
      action: PayloadAction<DateString>,
    ) {
      state.flags.isFetchingInstagramHashtagTrackedDate = false;
      state.trackedDate = action.payload;
    },
    receiveInstagramHashtagTrackedDateFailed(
      state,
      action: PayloadAction<string | undefined>,
    ) {
      state.flags.isFetchingInstagramHashtagTrackedDate = false;
      state.errors.fetchInstagramHashtagTrackedDateError = action.payload;
    },
    requestInstagramHashtagRemoveTrackedHashtags(state) {
      state.flags.isRemovingTrackeHashtags = true;
    },
    receiveIntagramHashtagRemoveTrackedHashtagsSucceed(
      state,
      action: PayloadAction<InstagramHashtag[]>,
    ) {
      state.flags.isRemovingTrackeHashtags = false;
      state.trackedHashtags = [...action.payload];
    },
    receiveInstagramHashtagRemoveTrackedHashtagsFailed(
      state,
      action: PayloadAction<string | undefined>,
    ) {
      state.flags.isRemovingTrackeHashtags = false;
      state.errors.removeTrackedHashtagsError = action.payload;
    },
    resetHashtagPageState(state) {
      return initialState;
    },
  },
});

export const fetchInstagramHashtagTrackedHashtags = createThunk(
  () => async (dispatch, getState) => {
    dispatch(actions.requestInstagramHashtagTrackedHashtags());

    try {
      const trackedHashtags = await instagramHashtagsApi.getTrackedHashtags();

      dispatch(
        actions.receiveInstagramHashtagTrackedHashtagsSucceed(
          trackedHashtags.data,
        ),
      );
    } catch (_e) {
      dispatch(
        actions.receiveInstagramHashtagTrackedHashtagsFailed(
          'An error occurred while fetching tracked hashtags. Please try again or contact Bazaarvoice Support.',
        ),
      );
    }
  },
);

export const fetchInstagramHashtagTrackedDate = createThunk(
  () => async (dispatch, getState) => {
    const { hashtag } = getState().instagram.hashtags.page;
    const userTimeZone = getUserTimeZone(getState());

    dispatch(actions.requestInstagramHashtagTrackedDate());

    try {
      const trackedDateData =
        await instagramHashtagsApi.getInitialTrackedDate(hashtag);

      dispatch(
        actions.receiveInstagramHashtagTrackedDateSucceed(
          DateString.fromTimestamp(trackedDateData.data, userTimeZone),
        ),
      );
    } catch (_e) {
      dispatch(
        actions.receiveInstagramHashtagTrackedDateFailed(
          'An error occurred while fetching the initial tracked date for this hashtag. Please try again or contact Bazaarvoice Support.',
        ),
      );
    }
  },
);

export const removeTrackedHashtags = createThunk(
  (hashtags: InstagramHashtag[]) => async (dispatch, getState) => {
    const { flags, trackedHashtags } = getState().instagram.hashtags.page;

    if (flags.isRemovingTrackeHashtags) {
      return;
    }

    dispatch(actions.requestInstagramHashtagRemoveTrackedHashtags());

    try {
      await instagramHashtagsApi.removeTrackedHashtags({ hashtags });

      const filteredTrackedHashtags = trackedHashtags.filter(
        (hashtag) => !hashtags.includes(hashtag),
      );

      dispatch(
        actions.receiveIntagramHashtagRemoveTrackedHashtagsSucceed(
          filteredTrackedHashtags,
        ),
      );
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Tracked Hashtags Updated',
          type: ToastType.SUCCESS,
          message: `You have successfully removed ${hashtags.join(
            ', ',
          )} from your list of tracked hastags`,
          options: {
            toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
          },
        }),
      );

      navigateToState('instagramAccount.summary', {}, { reload: true });
    } catch (_e) {
      dispatch(
        actions.receiveInstagramHashtagRemoveTrackedHashtagsFailed(
          'An error occurred while removing tracked hashtags. Please try again or contact Bazaarvoice Support.',
        ),
      );
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Error',
          type: ToastType.ERROR,
          message: 'Something went wrong, please try again.',
          options: {
            toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
          },
        }),
      );
    }
  },
);

export const {
  resetHashtagPageState,
  setInstagramHashtagDateRange,
  setInstagramHashtagPageSource,
} = actions;

export default reducer;
